import type { ComponentProps } from 'react';
import { Link as IntlLink } from '@/i18n/navigation';
import { toLiveHref } from '@/lib/toLiveHref';

type LinkProps = ComponentProps<typeof IntlLink>;
type AnchorProps = ComponentProps<'a'>;

/**
 * Why every internal link ships with `prefetch={false}`.
 *
 * `<Link>` normally fetches the next route's RSC payload as the link enters
 * the viewport, so the click is instant. That trade only pays when the
 * prefetch returns something the router can keep — and here it returns
 * nothing, because of two facts about this app:
 *
 * 1. `app/layout.tsx` is `force-dynamic`, so **every** route is dynamic.
 * 2. There is no `loading.tsx` anywhere in `app/`.
 *
 * Next's own matrix for that combination (`next/dist/docs/01-app/02-guides/
 * prefetching.md`) reads: prefetched — *no*; client cache TTL — *off*; server
 * roundtrip on click — *yes*. The request still goes out, the server answers
 * with no page data, and the click pays the full render regardless.
 *
 * Measured on a Standort page (2026-10-12, lokal.schuelerhilfe.de): scrolling
 * the two Standort link lists into view fired **46** RSC prefetches — 45.3 s
 * of cumulative origin time and 57 KB — and the subsequent click on an
 * already-prefetched link still took 4.8 s and issued its own fresh 18 KB RSC
 * request. The prefetches bought nothing and crowded out the page's images
 * while they ran.
 *
 * **Revisit this the moment either fact above changes.** Adding a
 * `loading.tsx`, or dropping `force-dynamic`, is exactly when prefetching
 * starts paying for itself — and this default would silently keep it off.
 * A caller that knows better can still pass `prefetch` explicitly.
 *
 * `false` covers hover as well as viewport, not just viewport
 * (`next/dist/docs/01-app/03-api-reference/02-components/link.md`).
 */
export const Link = ({ href, prefetch = false, ...rest }: LinkProps) => {
  if (typeof href !== 'string') {
    return <IntlLink href={href} prefetch={prefetch} {...rest} />;
  }

  const liveHref = toLiveHref(href);

  if (liveHref !== href || !href.startsWith('/') || href.startsWith('//')) {
    return <a href={liveHref} {...(rest as AnchorProps)} />;
  }

  return <IntlLink href={href} prefetch={prefetch} {...rest} />;
};
